/**
 * The link slot of one Submission Pack row (Task B4).
 *
 * A pack row links to EITHER a response artifact OR a document, never both —
 * `PackItem.link` is a discriminated union and the store's `linkItem` nulls the
 * other side on every write, so a body carrying both ids can never be sent.
 *
 * The artifact branch delegates to `ResponseLinkCell` rather than
 * reimplementing it: the Analysis lens already renders that exact chip for the
 * same underlying row, and two chips for one link would drift.
 *
 * Document previews open under context 'pack' (design decision D-12), so the
 * globally-mounted `DocumentPreviewModal` shows them full-screen; nothing docks
 * a reader on this page.
 */

import { useMemo, useRef, useState } from 'react'
import { Paperclip, X } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ResponseLinkCell } from '@/components/extraction/ResponseLinkCell'
import { ResponsePickerPopover } from '@/components/extraction/ResponsePickerPopover'
import { DocumentLinkPicker } from '@/components/pack/DocumentLinkPicker'
import { usePreviewStore } from '@/stores/preview-store'
import { formatFileSize, getAcceptedFileTypes } from '@/lib/schemas'
import type { PackItem } from '@/lib/api'

// ===================
// Types
// ===================

interface PackLinkCellProps {
  bidId: string
  item: PackItem
  readOnly?: boolean
  onLinkArtifact: (artifactId: string, sectionKey: string | null) => void | Promise<void>
  onLinkDocument: (documentId: string) => void | Promise<void>
  onUnlink: () => void | Promise<void>
  onCreateResponse: () => void | Promise<void>
  onUploadDocument: (file: File) => void | Promise<void>
}

/** Which panel the "Link / Upload" popover is showing. */
type PickerMode = 'menu' | 'response' | 'document'

// ===================
// Component
// ===================

export function PackLinkCell({
  bidId,
  item,
  readOnly = false,
  onLinkArtifact,
  onLinkDocument,
  onUnlink,
  onCreateResponse,
  onUploadDocument,
}: PackLinkCellProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [mode, setMode] = useState<PickerMode>('menu')
  const fileInputRef = useRef<HTMLInputElement>(null)

  // The plain-input form of the dropzone allow-list, same expression the bid
  // document uploader uses. It only filters the OS picker — `validateFile` on
  // the way out is what actually enforces the rule.
  const acceptString = useMemo(
    () =>
      Object.entries(getAcceptedFileTypes())
        .flatMap(([mimeType, extensions]) => [mimeType, ...extensions])
        .join(','),
    []
  )

  const link = item.link

  if (link?.kind === 'artifact') {
    return (
      <ResponseLinkCell
        bidId={bidId}
        linkedArtifactId={link.artifactId}
        linkedSectionKey={link.sectionKey}
        linkedArtifactName={link.artifactName}
        linkedSectionTitle={link.sectionTitle}
        readOnly={readOnly}
        fillWidth
        onLink={onLinkArtifact}
        onUnlink={onUnlink}
        onCreate={onCreateResponse}
      />
    )
  }

  if (link?.kind === 'document') {
    return (
      <div className="group/link flex items-center gap-1">
        <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <button
          type="button"
          className="min-w-0 flex-1 truncate text-left text-xs text-[hsl(var(--link))] hover:underline"
          title={link.documentName}
          onClick={() =>
            void usePreviewStore.getState().openPreview({
              documentId: link.documentId,
              documentName: link.documentName,
              context: 'pack',
            })
          }
        >
          {link.documentName}
        </button>
        {link.sizeBytes !== null && (
          <span className="text-meta shrink-0">{formatFileSize(link.sizeBytes)}</span>
        )}
        {!readOnly && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                type="button"
                className="shrink-0 rounded-sm p-0.5 opacity-0 transition-opacity hover:bg-muted group-hover/link:opacity-100"
                title="Remove link"
                aria-label={`Remove link for ${item.title}`}
              >
                <X className="h-3 w-3 text-subtle hover:text-foreground" />
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent className="max-w-xs">
              <AlertDialogTitle className="text-sm">Remove link?</AlertDialogTitle>
              <AlertDialogDescription className="text-xs">
                This will unlink the document from this submission item. The document
                itself stays where it is, and you can re-link it later.
              </AlertDialogDescription>
              <AlertDialogFooter>
                <AlertDialogCancel className="h-8 text-xs">Cancel</AlertDialogCancel>
                <AlertDialogAction className="h-8 text-xs" onClick={() => void onUnlink()}>
                  Remove
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    )
  }

  if (readOnly) {
    return <span className="text-meta">—</span>
  }

  const closePicker = () => {
    setIsOpen(false)
    setMode('menu')
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept={acceptString}
        aria-label={`Upload a file for ${item.title}`}
        onChange={(event) => {
          const file = event.target.files?.[0]
          // Reset first so re-picking the same file still fires `change`.
          event.target.value = ''
          if (file) void onUploadDocument(file)
        }}
      />

      <Popover
        open={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open)
          if (!open) setMode('menu')
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            className="group/trigger flex items-center gap-1.5 text-meta transition-colors hover:text-foreground"
          >
            {/* Always visible: it is the column's leading glyph, like the link
                icon on a linked row, so the "File" heading lines up over it. */}
            <Paperclip className="h-3 w-3 shrink-0" aria-hidden="true" />
            <span className="group-hover/trigger:underline">Link / Upload</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          {mode === 'response' ? (
            <ResponsePickerPopover
              bidId={bidId}
              onSelect={(artifactId, sectionKey) => {
                closePicker()
                return onLinkArtifact(artifactId, sectionKey)
              }}
              onCreate={() => {
                closePicker()
                return onCreateResponse()
              }}
            />
          ) : mode === 'document' ? (
            <DocumentLinkPicker
              bidId={bidId}
              onSelect={(documentId) => {
                closePicker()
                return onLinkDocument(documentId)
              }}
            />
          ) : (
            <div className="w-56 p-1">
              <Button
                type="button"
                variant="ghost"
                className="w-full justify-start px-2"
                onClick={() => setMode('response')}
              >
                Link response
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full justify-start px-2"
                onClick={() => setMode('document')}
              >
                Pick from documents
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full justify-start px-2"
                onClick={() => {
                  closePicker()
                  fileInputRef.current?.click()
                }}
              >
                Upload file
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </>
  )
}
